/**
 * UCM 入向「人工粘贴解析」面板
 * 粘贴报文文本 → 调后端解析成待确认记录；失败时列出未识别行
 */

import React, { useState } from 'react';
import { Card, Input, Button, Space, Alert, Typography, Tag, message } from 'antd';
import { EyeOutlined, ThunderboltOutlined, UndoOutlined } from '@ant-design/icons';
import { ucmService } from '../../../../api/enhancedApi';
import {
    UCM_INBOUND_DIRECTION,
    UCM_INBOUND_STATUS,
} from '../constants/ucmConstants';

const { TextArea } = Input;
const { Text } = Typography;

const UcmInboundParsePanel = ({ onParsed, onViewExisting, onReopenExisting }) => {
    const [raw, setRaw] = useState('');
    const [parsing, setParsing] = useState(false);
    const [result, setResult] = useState(null);

    const handleParse = async () => {
        if (!raw.trim()) {
            message.warning('请先粘贴 UCM 报文文本');
            return;
        }
        setParsing(true);
        setResult(null);
        try {
            const resp = await ucmService.parseUcmInbound({ raw_message: raw });
            const data = resp.data || {};
            setResult(data);
            const createdCount = (data.created || []).length;
            if (createdCount > 0) {
                message.success(data.message || `解析成功，生成 ${createdCount} 条待确认记录`);
                onParsed && onParsed();
            } else if (data.parse_failed) {
                message.error('解析失败，请检查报文');
            } else {
                message.info(data.message || '未识别到 UCM 报文段');
            }
        } catch (error) {
            message.error(error.response?.data?.detail || '解析失败');
        } finally {
            setParsing(false);
        }
    };

    const handleClear = () => {
        setRaw('');
        setResult(null);
    };

    return (
        <Card size="small" title="人工粘贴解析" style={{ marginBottom: 16 }}>
            <TextArea
                rows={6}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={'把整封 UCM 报文（含报文头 航班/日.注册.站、OUT/IN、板号行）粘贴到这里，可含签名与免责声明'}
                style={{ fontFamily: 'Courier New, monospace' }}
            />
            <Space style={{ marginTop: 12 }}>
                <Button type="primary" icon={<ThunderboltOutlined />} loading={parsing} onClick={handleParse}>
                    解析
                </Button>
                <Button onClick={handleClear} disabled={parsing}>清空</Button>
            </Space>

            {result && (result.created || []).length > 0 && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="success"
                    showIcon
                    message={result.message || `生成 ${(result.created || []).length} 条待确认记录`}
                    description={(result.duplicates || []).length > 0 ? `另有 ${(result.duplicates).length} 条为重复报文，已去重未新建` : null}
                />
            )}

            {result && (result.duplicates || []).length > 0 && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="warning"
                    showIcon
                    message={`命中 ${(result.duplicates || []).length} 条已有记录，未创建重复记录`}
                    description={
                        <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
                            {(result.duplicates || []).map((pending) => {
                                const status = UCM_INBOUND_STATUS[pending.status] || {};
                                const direction = UCM_INBOUND_DIRECTION[pending.direction] || {};
                                return (
                                    <Space
                                        key={pending.id}
                                        wrap
                                        style={{ width: '100%', justifyContent: 'space-between' }}
                                    >
                                        <Space wrap>
                                            <Text strong>记录 #{pending.id}</Text>
                                            <Tag color={direction.color}>{direction.label || pending.direction || '方向未知'}</Tag>
                                            <Tag color={status.color}>{status.label || pending.status}</Tag>
                                            <Text type="secondary">
                                                {pending.status === 'ignored'
                                                    ? '该报文已存在且已忽略'
                                                    : pending.status === 'pending'
                                                        ? '该报文正在待处理'
                                                        : pending.status === 'confirmed'
                                                            ? '该报文已经确认'
                                                            : '该报文已存在'}
                                            </Text>
                                        </Space>
                                        <Space>
                                            <Button
                                                size="small"
                                                type="link"
                                                icon={<EyeOutlined />}
                                                onClick={() => onViewExisting && onViewExisting(pending)}
                                            >
                                                查看原记录
                                            </Button>
                                            {pending.status === 'ignored' && (
                                                <Button
                                                    size="small"
                                                    type="link"
                                                    icon={<UndoOutlined />}
                                                    onClick={() => onReopenExisting && onReopenExisting(pending)}
                                                >
                                                    重新处理
                                                </Button>
                                            )}
                                        </Space>
                                    </Space>
                                );
                            })}
                        </Space>
                    }
                />
            )}

            {result && result.parse_failed && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="error"
                    showIcon
                    message="未能从报文中解析出有效内容（航班 / 方向 / 板号）"
                    description={
                        (result.unrecognized_lines || []).length > 0 ? (
                            <div style={{ maxHeight: 160, overflow: 'auto' }}>
                                <Text type="secondary">未识别的行：</Text>
                                <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>
                                    {(result.unrecognized_lines || []).join('\n')}
                                </pre>
                            </div>
                        ) : '请核对是否为标准 UCM 报文'
                    }
                />
            )}

            {result && !result.parse_failed && (result.created || []).length === 0 && (result.duplicates || []).length === 0 && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="info"
                    showIcon
                    message={result.message || '未识别到 UCM 报文段，未生成记录'}
                />
            )}
        </Card>
    );
};

export default UcmInboundParsePanel;
